import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { invalidateAfterMutation } from "@/core/api/invalidate-after-mutation";
import { QUERY_KEYS } from "@/core/api/query-keys";
import { logger } from "@/core/utils/func/logger";
import { merchantEndpoints } from "@/features/merchant/api/endpoints";
import type {
  CreateMerchantFeeOverridePayload,
  MerchantFeeOverrideDto,
} from "@/features/merchant/api/dto";

/**
 * Ajoute une dérogation tarifaire (`POST /MerchantFeeOverrides/add`) —
 * back-office (flashpay-superadmin, flashpay-admin).
 */
export const useCreateFeeOverride = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["merchant", "fee-overrides", "create"],
    mutationFn: (body: CreateMerchantFeeOverridePayload) =>
      mutateData({
        url: merchantEndpoints.merchantFeeOverrides.add,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<MerchantFeeOverrideDto>,
    onSuccess: (_data, variables) => {
      return invalidateAfterMutation(
        queryClient,
        [QUERY_KEYS.MERCHANT_FEE_OVERRIDES(variables.merchantId)],
        { resetListPage: true }
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCreateFeeOverride failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "merchant" }
      );
    },
  });
};
